import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'iriro-basic-file-upload, shared-ui-basic-file-upload',
  standalone: true,
  imports: [CommonModule, FileUploadModule, ButtonModule, TooltipModule],
  template: `
    <div class="basic-file-upload">
      @if (!selectedFiles.length) {
        <p-fileUpload
          #fileUploader
          mode="basic"
          [multiple]="multiple"
          [accept]="accept"
          [auto]="false"
          [customUpload]="true"
          [chooseLabel]="chooseLabel"
          [chooseIcon]="chooseIcon"
          [styleClass]="styleClass"
          (onSelect)="onFileSelect($event)"
          (uploadHandler)="onUploadHandler($event)">
        </p-fileUpload>
      } @else {
        <div class="selected-file-row">
          <i class="pi pi-file"></i>
          <span
            class="file-name"
            [pTooltip]="getFullFileNames()"
            tooltipPosition="top">
            {{ getDisplayFileName() }}
          </span>
          <button
            pButton
            type="button"
            icon="pi pi-times"
            class="p-button-text p-button-rounded p-button-sm"
            [pTooltip]="'Remove'"
            tooltipPosition="top"
            (click)="clear()">
          </button>
          <button
            pButton
            type="button"
            [label]="uploadLabel"
            [icon]="uploadIcon"
            class="p-button-sm"
            [disabled]="uploading"
            (click)="upload()">
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .basic-file-upload {
      display: inline-flex;
      align-items: center;
    }
    .selected-file-row {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0rem 0.4rem;
      border-radius: 8px;
      background: var(--p-content-background);
      box-shadow: 0 0 10px 0 #0000000f;
    }
    .selected-file-row .pi-file {
      color: var(--p-primary-color);
    }
    .file-name {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.9rem;
    }
  `]
})
export class BasicFileUploadComponent {
  @Input() chooseLabel = 'Choose';
  @Input() uploadLabel = 'Upload';
  @Input() chooseIcon = 'pi pi-plus';
  @Input() uploadIcon = 'pi pi-upload';
  @Input() accept: string | undefined;
  @Input() multiple = false;
  @Input() styleClass = '';
  @Input() maxFileNameLength = 30;

  @Output() uploadHandler = new EventEmitter<FileUploadHandlerEvent>();

  @ViewChild('fileUploader') fileUploader: FileUpload | undefined;

  selectedFiles: File[] = [];
  uploading = false;

  onFileSelect(event: any) {
    this.selectedFiles = event.currentFiles || event.files || [];
  }

  onUploadHandler(event: FileUploadHandlerEvent) {
    this.uploadHandler.emit(event);
  }

  upload() {
    if (this.selectedFiles.length > 0) {
      this.uploading = true;
      this.uploadHandler.emit({ files: this.selectedFiles });
      this.uploading = false;
      this.clear();
    }
  }

  clear() {
    this.selectedFiles = [];
    this.fileUploader?.clear();
  }

  getDisplayFileName(): string {
    if (!this.selectedFiles.length) return '';
    if (this.selectedFiles.length === 1) {
      const name = this.selectedFiles[0].name;
      if (name.length <= this.maxFileNameLength) return name;
      return name.substring(0, this.maxFileNameLength) + '...';
    }
    return `${this.selectedFiles.length} files`;
  }

  getFullFileNames(): string {
    return this.selectedFiles.map(f => f.name).join('\n');
  }
}
